import { Card, Field, SectionLabel, Select } from '@wundo/ui';
import type { NotificationChannel } from '@wundo/shared/notificationChannel.ts';
import { LanguageSelect } from '@/components/LanguageSelect.tsx';
import { CohortSearchSelect } from '@/components/pickers/CohortPicker.tsx';
import { OrganisationPicker } from '@/components/pickers/OrganisationPicker.tsx';
import { useState } from 'react';
import type { CohortWithOrganisation } from '@/types/cohorts.ts';
import type { Organisation } from '@/types/organisations.ts';
import { useActiveOrganisation } from '@/lib/activeOrganisation.ts';
import { useResetOnChange } from '@/lib/useResetOnChange.ts';
import { AUTOMATIC_LANGUAGE } from '@/utils/language.ts';

const CHANNEL_OPTIONS = [
  { value: 'web', label: 'Web' },
  { value: 'msteams', label: 'MS Teams' },
  { value: 'slack', label: 'Slack' },
];

/* Where meeting notifications go, for everyone in this import. */
const NOTIFICATION_CHANNEL_OPTIONS = [
  { value: 'email', label: 'E-mail' },
  { value: 'msteams', label: 'MS Teams' },
  { value: 'slack', label: 'Slack' },
  { value: 'none', label: 'None' },
];

/** Step 2 of a bulk import: the organisation and cohort, and the settings every person gets. */
export function ImportTargetCard({ target }: { target: ImportTarget }) {
  return (
    <Card as='section' className='flex flex-col gap-4'>
      <SectionLabel as='h2'>Step 2: Select organisation and cohort</SectionLabel>

      <div className='grid gap-4 mobile:grid-cols-2'>
        <Field label='Organisation' required htmlFor='organisation'>
          <OrganisationPicker
            id='organisation'
            value={target.organisation}
            onChange={target.pickOrganisation}
            placeholder='Search organisations...'
            clearable
          />
        </Field>

        <Field
          label='Cohort'
          required
          htmlFor='cohort'
          description='Select the cohort to add users to'
        >
          <CohortSearchSelect
            id='cohort'
            value={target.cohort}
            onChange={target.setCohort}
            organisationId={target.organisation?.id ?? null}
            disabled={target.organisation === null}
          />
        </Field>

        <Field
          label='Channel'
          required
          htmlFor='channel'
          description='Communication channel for all users'
        >
          <Select
            id='channel'
            wrapperClassName='w-full'
            className='w-full'
            options={CHANNEL_OPTIONS}
            value={target.channel}
            onChange={(event) => target.setChannel(event.target.value as ImportChannel)}
          />
        </Field>

        <Field
          label='Notification channel'
          required
          htmlFor='notification_channel'
          description='Where meeting notifications are sent for all users'
        >
          <Select
            id='notification_channel'
            wrapperClassName='w-full'
            className='w-full'
            options={NOTIFICATION_CHANNEL_OPTIONS}
            value={target.notificationChannel}
            onChange={(event) =>
              target.setNotificationChannel(event.target.value as NotificationChannel)
            }
          />
        </Field>

        <Field
          label='Language'
          required
          htmlFor='language'
          description='Automatic takes the language of their Teams or browser on first contact'
        >
          <LanguageSelect
            id='language'
            allowAutomatic
            className='w-full'
            value={target.language}
            onChange={target.setLanguage}
          />
        </Field>
      </div>
    </Card>
  );
}

type ImportChannel = 'web' | 'msteams' | 'slack';

export interface ImportTarget {
  organisation: Organisation | null;
  /** A cohort belongs to one organisation, so switching organisation drops it. */
  pickOrganisation: (organisation: Organisation | null) => void;
  cohort: CohortWithOrganisation | null;
  setCohort: (cohort: CohortWithOrganisation | null) => void;
  channel: ImportChannel;
  setChannel: (channel: ImportChannel) => void;
  notificationChannel: NotificationChannel;
  setNotificationChannel: (channel: NotificationChannel) => void;
  language: string;
  setLanguage: (language: string) => void;
}

export function useImportTarget(): ImportTarget {
  /* Both start over when the header's organisation changes; a cohort belongs to one. */
  const headerOrganisation = useActiveOrganisation().organisation;
  const headerKey = headerOrganisation?.id ?? '';
  const [organisation, setOrganisation] = useState(headerOrganisation);
  const [cohort, setCohort] = useState<CohortWithOrganisation | null>(null);
  useResetOnChange(headerKey, () => {
    setOrganisation(headerOrganisation);
    setCohort(null);
  });
  const [channel, setChannel] = useState<ImportChannel>('msteams');
  const [notificationChannel, setNotificationChannel] =
    useState<NotificationChannel>('email');
  const [language, setLanguage] = useState(AUTOMATIC_LANGUAGE);

  return {
    organisation,
    pickOrganisation: (picked) => {
      setOrganisation(picked);
      setCohort(null);
    },
    cohort,
    setCohort,
    channel,
    setChannel,
    notificationChannel,
    setNotificationChannel,
    language,
    setLanguage,
  };
}
